import { useState, useEffect, useCallback, useRef } from 'react';
import { Badges, Svg } from '../../../snippets';
import { ProductImage } from './ProductImage';
import { ProductVideo } from './ProductVideo';
import { ProductMediaSlider } from './ProductMediaSlider';
import { ProductMediaCalloutGrid } from './ProductMediaCalloutGrid';
import { useOpenMediaModal } from './useOpenMediaModal';

function Thumb({ media, index, activeIndex, size, onSelect }) {
  const isActive = index === activeIndex;
  return (
    <button
      aria-label={`Select media ${index + 1}`}
      onClick={() => onSelect(index)}
      className={`shrink-0 border-2 ${
        isActive ? 'border-primary' : 'hover:border-gray-300 border-transparent'
      }`}
      type="button"
    >
      <div className={`${size} aspect-square overflow-hidden`}>
        {media.mediaContentType === 'VIDEO' ? (
          <ProductVideo
            media={media}
            muted
            loop
            className="h-full w-full object-cover"
          />
        ) : (
          <ProductImage media={media} fill className="object-cover" />
        )}
      </div>
    </button>
  );
}

export function ProductMediaGallery({ selectedMedia = [], selectedVariant }) {
  const { handleModal } = useOpenMediaModal();
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiper, setSwiper] = useState(null);
  const [thumbOverflow, setThumbOverflow] = useState(false);
  const thumbRailRef = useRef(null);
  const active = selectedMedia[activeIndex];

  /* reset thumb when colour changes */
  useEffect(
    () => setActiveIndex(0),
    [selectedVariant?.selectedOptionsMap?.Color]
  );

  /* rail overflow set */
  useEffect(() => {
    const el = thumbRailRef.current;
    if (el) setThumbOverflow(el.scrollHeight > el.clientHeight);
  }, [selectedMedia]);

  const openModal = useCallback(() => {
    if (!active || active.mediaContentType === 'VIDEO') return;
    handleModal({ imageSrc: active.image.src, altText: active.image.altText });
  }, [active, handleModal]);

  const scrollThumbs = () =>
    thumbRailRef.current?.scrollBy({ top: 300, behavior: 'smooth' });

  const jumpTo = (i) => {
    setActiveIndex(i);
    if (swiper) {
      swiper.params.loop ? swiper.slideToLoop(i, 0) : swiper.slideTo(i, 0);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[96px_1fr] xl:gap-8">
      {/* Desktop thumbnail previews/rail */}
      <div className="order-0 relative hidden xl:block">
        <div
          ref={thumbRailRef}
          className="max-h-[calc(100vh-200px)] overflow-y-auto pr-1"
        >
          {selectedMedia.map((m, i) => (
            <Thumb
              key={m.id}
              media={m}
              index={i}
              activeIndex={activeIndex}
              size="h-20 w-20"
              onSelect={jumpTo}
            />
          ))}
        </div>

        {thumbOverflow && (
          <button
            onClick={scrollThumbs}
            aria-label="Scroll thumbnails down"
            className="absolute bottom-0 left-1/2 -translate-x-1/2 p-2"
            type="button"
          >
            <Svg
              src="/svgs/arrow-down.svg#arrow-down"
              className="hover:text-gray-800 h-6 w-6 text-mediumGray"
              viewBox="0 0 24 24"
            />
          </button>
        )}
      </div>

      {/* Main/currently selected pdp image */}
      <div className="relative order-1 bg-[#E6F0F8]">
        {active && (
          <button
            onClick={openModal}
            aria-label="Open product image in modal"
            className="hidden w-full p-9 xl:block"
            type="button"
          >
            {active.mediaContentType === 'VIDEO' ? (
              <ProductVideo media={active} />
            ) : (
              <ProductImage media={active} priority />
            )}
          </button>
        )}

        {/* Mobile thumbnail previews/rail */}
        <div className="xl:hidden">
          <div className="relative">
            <ProductMediaSlider
              memoizedProductMedia={selectedMedia}
              onSlideChange={setActiveIndex}
              onSwiper={setSwiper}
            />
            <button
              onClick={() => swiper?.slidePrev()}
              aria-label="Previous image"
              className="absolute left-4 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow"
              type="button"
            >
              <Svg
                src="/svgs/arrow-left.svg#arrow-left"
                className="hover:text-gray-800 h-4 w-4 text-mediumGray"
                viewBox="0 0 24 24"
              />
            </button>
            <button
              onClick={() => swiper?.slideNext()}
              aria-label="Next image"
              className="absolute right-4 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow"
              type="button"
            >
              <Svg
                src="/svgs/arrow-right.svg#arrow-right"
                className="hover:text-gray-800 h-4 w-4 text-mediumGray"
                viewBox="0 0 24 24"
              />
            </button>
          </div>

          <div className="mt-4 flex gap-3 overflow-x-auto whitespace-nowrap scrollbar-hide">
            {selectedMedia.map((m, i) => (
              <Thumb
                key={m.id}
                media={m}
                index={i}
                activeIndex={activeIndex}
                size="h-16 w-16"
                onSelect={jumpTo}
              />
            ))}
          </div>
        </div>

        <div className="pointer-events-none absolute right-4 top-4 z-[1]">
          <Badges tags={selectedVariant?.product?.tags} />
        </div>
      </div>

      <div className="order-2 xl:col-span-2">
        <ProductMediaCalloutGrid />
      </div>
    </div>
  );
}

ProductMediaGallery.displayName = 'ProductMediaGallery';
